import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { extractViewBox, isValidSvg } from "../../utils/svgUtils";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";

interface UploadedSvg {
    name: string;
    content: string;
}

interface MarkedCell {
    x: number;
    y: number;
}

export interface WrapOutlineSegment {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

function cellKey(col: number, row: number): string {
    return `${col},${row}`;
}

/** Edges between marked and unmarked cells (outer boundary of the marked region). */
export function computeWrapOutlineSegments(
    markedCells: MarkedCell[],
    gridOriginX: number,
    gridOriginY: number,
    cellSize: number,
    gridCols: number,
    gridRows: number,
): WrapOutlineSegment[] {
    const marked = new Set<string>();
    for (const c of markedCells) {
        const col = Math.round((c.x - gridOriginX) / cellSize);
        const row = Math.round((c.y - gridOriginY) / cellSize);
        if (col < 0 || col >= gridCols || row < 0 || row >= gridRows) continue;
        marked.add(cellKey(col, row));
    }

    const neighborMarked = (col: number, row: number) =>
        col >= 0 &&
        col < gridCols &&
        row >= 0 &&
        row < gridRows &&
        marked.has(cellKey(col, row));

    const segs: WrapOutlineSegment[] = [];
    for (const key of marked) {
        const comma = key.indexOf(",");
        const col = Number(key.slice(0, comma));
        const row = Number(key.slice(comma + 1));
        const x = gridOriginX + col * cellSize;
        const y = gridOriginY + row * cellSize;
        const xr = x + cellSize;
        const yb = y + cellSize;
        if (!neighborMarked(col, row - 1)) {
            segs.push({ x1: x, y1: y, x2: xr, y2: y });
        }
        if (!neighborMarked(col + 1, row)) {
            segs.push({ x1: xr, y1: y, x2: xr, y2: yb });
        }
        if (!neighborMarked(col, row + 1)) {
            segs.push({ x1: xr, y1: yb, x2: x, y2: yb });
        }
        if (!neighborMarked(col - 1, row)) {
            segs.push({ x1: x, y1: yb, x2: x, y2: y });
        }
    }
    return segs;
}

interface GridCellWrapperState {
    inputSvg: UploadedSvg | null;
    cellSize: number;
    bufferCells: number;
    gridStroke: string;
    gridStrokeOpacity: number;
    markedFill: string;
    markedFillOpacity: number;
    wrapStroke: string;
    wrapStrokeWidth: number;
    wrapStrokeOpacity: number;
    wrapStrokeActive: boolean;
    wrapOutlineSegments: WrapOutlineSegment[];
    markedCells: MarkedCell[];
    gridCols: number;
    gridRows: number;
    viewBoxAttr: string;
    canExport: boolean;
    error: string | null;
    setCellSize: (v: number) => void;
    setBufferCells: (v: number) => void;
    setGridStroke: (v: string) => void;
    setGridStrokeOpacity: (v: number) => void;
    setMarkedFill: (v: string) => void;
    setMarkedFillOpacity: (v: number) => void;
    setWrapStroke: (v: string) => void;
    setWrapStrokeWidth: (v: number) => void;
    setWrapStrokeOpacity: (v: number) => void;
    setError: (v: string | null) => void;
    handleSvgLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    inputRef: React.RefObject<HTMLInputElement>;
}

const GridCellWrapperContext = createContext<GridCellWrapperState | null>(null);

export function useGridCellWrapper() {
    const context = useContext(GridCellWrapperContext);
    if (!context) {
        throw new Error(
            "useGridCellWrapper must be used within GridCellWrapperProvider",
        );
    }
    return context;
}

interface GridCellWrapperProviderProps {
    children: React.ReactNode;
}

function clamp01(v: number): number {
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, Math.min(1, v));
}

interface PreparedElement {
    el: SVGGeometryElement;
    inv: DOMMatrix;
    // Axis-aligned bounding box in viewBox (world) coordinates, inflated by half stroke width.
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

interface MountedGeom {
    host: HTMLDivElement;
    elements: PreparedElement[];
}

const SAMPLE_OFFSETS = [0.15, 0.5, 0.85];

export const MIN_GRID_CELL_SIZE = 2;

/**
 * Dilate a boolean grid in-place by `buffer` cells in all directions using a
 * separable sliding-window pass. Runs in O(rows * cols) regardless of buffer.
 */
function dilateGrid(
    grid: Uint8Array,
    cols: number,
    rows: number,
    buffer: number,
): Uint8Array {
    if (buffer <= 0) return grid;
    const tmp = new Uint8Array(grid.length);

    // Horizontal pass: tmp[r,c] = any(grid[r, c-buffer .. c+buffer])
    for (let r = 0; r < rows; r++) {
        const rowStart = r * cols;
        let count = 0;
        const initEnd = Math.min(cols - 1, buffer);
        for (let k = 0; k <= initEnd; k++) {
            if (grid[rowStart + k]) count++;
        }
        for (let c = 0; c < cols; c++) {
            tmp[rowStart + c] = count > 0 ? 1 : 0;
            const leaving = c - buffer;
            const entering = c + buffer + 1;
            if (leaving >= 0 && grid[rowStart + leaving]) count--;
            if (entering < cols && grid[rowStart + entering]) count++;
        }
    }

    // Vertical pass: out[r,c] = any(tmp[r-buffer .. r+buffer, c])
    const out = new Uint8Array(grid.length);
    for (let c = 0; c < cols; c++) {
        let count = 0;
        const initEnd = Math.min(rows - 1, buffer);
        for (let k = 0; k <= initEnd; k++) {
            if (tmp[k * cols + c]) count++;
        }
        for (let r = 0; r < rows; r++) {
            out[r * cols + c] = count > 0 ? 1 : 0;
            const leaving = r - buffer;
            const entering = r + buffer + 1;
            if (leaving >= 0 && tmp[leaving * cols + c]) count--;
            if (entering < rows && tmp[entering * cols + c]) count++;
        }
    }

    return out;
}

function elementOverlapsCell(
    e: PreparedElement,
    cellX: number,
    cellY: number,
    cellSize: number,
): boolean {
    for (let yi = 0; yi < SAMPLE_OFFSETS.length; yi++) {
        const worldY = cellY + cellSize * SAMPLE_OFFSETS[yi];
        for (let xi = 0; xi < SAMPLE_OFFSETS.length; xi++) {
            const worldX = cellX + cellSize * SAMPLE_OFFSETS[xi];
            const lp = new DOMPoint(worldX, worldY).matrixTransform(e.inv);
            if (e.el.isPointInFill(lp) || e.el.isPointInStroke(lp)) {
                return true;
            }
        }
    }
    return false;
}

function prepareMountedSvg(svgMarkup: string): MountedGeom | null {
    if (typeof document === "undefined") return null;
    const doc = new DOMParser().parseFromString(svgMarkup, "image/svg+xml");
    const parsed = doc.documentElement;
    if (!(parsed instanceof SVGSVGElement)) return null;
    if (parsed.tagName.toLowerCase() === "parsererror") return null;

    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
        "position:fixed;left:0;top:0;width:512px;height:512px;opacity:0;pointer-events:none;z-index:-1;overflow:hidden";

    const imported = document.importNode(parsed, true) as SVGSVGElement;
    const vbAttr = imported.getAttribute("viewBox");
    if (vbAttr) {
        const vbParts = vbAttr.trim().split(/[\s,]+/).map(Number);
        if (
            vbParts.length === 4 &&
            vbParts.every((n) => Number.isFinite(n)) &&
            vbParts[2] > 0 &&
            vbParts[3] > 0
        ) {
            imported.setAttribute("width", String(vbParts[2]));
            imported.setAttribute("height", String(vbParts[3]));
        }
    }
    host.appendChild(imported);
    document.body.appendChild(host);

    const nodes = imported.querySelectorAll<SVGGeometryElement>(
        "path,rect,circle,ellipse,polygon,polyline,line",
    );
    const elements: PreparedElement[] = [];
    nodes.forEach((node) => {
        if (!(node instanceof SVGGeometryElement)) return;
        const ctm = node.getCTM();
        if (!ctm) return;
        let inv: DOMMatrix;
        try {
            inv = ctm.inverse();
        } catch {
            return;
        }
        let bb: DOMRect;
        try {
            bb = node.getBBox();
        } catch {
            return;
        }

        let strokeInflate = 0;
        try {
            const cs = window.getComputedStyle(node);
            if (cs.stroke && cs.stroke !== "none") {
                const sw = parseFloat(cs.strokeWidth);
                if (Number.isFinite(sw) && sw > 0) strokeInflate = sw / 2;
            }
        } catch {
            // ignore
        }

        const localCorners = [
            new DOMPoint(bb.x - strokeInflate, bb.y - strokeInflate),
            new DOMPoint(bb.x + bb.width + strokeInflate, bb.y - strokeInflate),
            new DOMPoint(bb.x - strokeInflate, bb.y + bb.height + strokeInflate),
            new DOMPoint(
                bb.x + bb.width + strokeInflate,
                bb.y + bb.height + strokeInflate,
            ),
        ];
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        for (let i = 0; i < localCorners.length; i++) {
            const p = localCorners[i].matrixTransform(ctm);
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
        }
        if (!Number.isFinite(minX) || !Number.isFinite(minY)) return;

        elements.push({ el: node, inv, minX, minY, maxX, maxY });
    });

    return { host, elements };
}

export function GridCellWrapperProvider({ children }: GridCellWrapperProviderProps) {
    const [inputSvg, setInputSvg] = useState<UploadedSvg | null>(null);
    const [cellSize, setCellSizeState] = useState(20);
    const setCellSize = useCallback((v: number) => {
        if (!Number.isFinite(v)) return;
        setCellSizeState(Math.max(MIN_GRID_CELL_SIZE, v));
    }, []);
    const [bufferCells, setBufferCellsState] = useState(0);
    const setBufferCells = useCallback((v: number) => {
        if (!Number.isFinite(v)) return;
        setBufferCellsState(Math.max(0, Math.floor(v)));
    }, []);
    const [gridStroke, setGridStroke] = useState("#4a4a4a");
    const [gridStrokeOpacity, setGridStrokeOpacity] = useState(0.35);
    const [markedFill, setMarkedFill] = useState("#4f8cff");
    const [markedFillOpacity, setMarkedFillOpacity] = useState(0.5);
    const [wrapStroke, setWrapStroke] = useState("#1a1a1a");
    const [wrapStrokeWidth, setWrapStrokeWidthState] = useState(0);
    const setWrapStrokeWidth = useCallback((v: number) => {
        if (!Number.isFinite(v)) return;
        setWrapStrokeWidthState(Math.max(0, Math.min(64, v)));
    }, []);
    const [wrapStrokeOpacity, setWrapStrokeOpacityState] = useState(1);
    const setWrapStrokeOpacity = useCallback((v: number) => {
        if (!Number.isFinite(v)) return;
        setWrapStrokeOpacityState(clamp01(v));
    }, []);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const sourceViewBox = useMemo(() => {
        if (!inputSvg) return null;
        return extractViewBox(inputSvg.content)?.viewBox ?? null;
    }, [inputSvg]);

    const sourceViewBoxAttr = sourceViewBox ?? "0 0 200 200";
    const [vbX, vbY, vbW, vbH] = useMemo(() => {
        const parsed = sourceViewBoxAttr.split(/\s+/).map(Number);
        if (parsed.length !== 4 || !parsed.every((n) => Number.isFinite(n))) {
            return [0, 0, 200, 200] as const;
        }
        return parsed as [number, number, number, number];
    }, [sourceViewBoxAttr]);

    const safeCellSize = Math.max(
        MIN_GRID_CELL_SIZE,
        Number.isFinite(cellSize) ? cellSize : MIN_GRID_CELL_SIZE,
    );
    const safeWrapStrokeWidth = Math.max(
        0,
        Math.min(64, Number.isFinite(wrapStrokeWidth) ? wrapStrokeWidth : 0),
    );
    const wrapStrokeOpacityClamped = clamp01(wrapStrokeOpacity);
    const wrapStrokeActive =
        safeWrapStrokeWidth > 0 &&
        wrapStroke.trim() !== "" &&
        wrapStroke.trim().toLowerCase() !== "none";
    const coreCols = Math.max(1, Math.ceil(vbW / safeCellSize));
    const coreRows = Math.max(1, Math.ceil(vbH / safeCellSize));
    // Grid is expanded outward by `bufferCells` on each side so dilated cells
    // have room to live past the source viewBox.
    const gridCols = coreCols + 2 * bufferCells;
    const gridRows = coreRows + 2 * bufferCells;
    const gridOriginX = vbX - bufferCells * safeCellSize;
    const gridOriginY = vbY - bufferCells * safeCellSize;
    const gridWidth = gridCols * safeCellSize;
    const gridHeight = gridRows * safeCellSize;
    const viewBoxAttr = `${gridOriginX} ${gridOriginY} ${gridWidth} ${gridHeight}`;

    // Mount the SVG once per inputSvg and precompute per-element geometry
    // (CTM inverse and world-space AABB). This avoids re-parsing the SVG and
    // re-flushing layout every time cellSize or grid dimensions change.
    const [mountedGeom, setMountedGeom] = useState<MountedGeom | null>(null);

    useEffect(() => {
        if (!inputSvg) {
            setMountedGeom(null);
            return;
        }
        let prepared: MountedGeom | null = null;
        try {
            prepared = prepareMountedSvg(inputSvg.content);
        } catch {
            prepared = null;
        }
        setMountedGeom(prepared);

        return () => {
            if (prepared?.host.parentNode) {
                prepared.host.parentNode.removeChild(prepared.host);
            }
        };
    }, [inputSvg]);

    const markedCells = useMemo(() => {
        if (!mountedGeom) return [] as MarkedCell[];

        const elements = mountedGeom.elements;
        if (elements.length === 0) return [] as MarkedCell[];

        let grid: Uint8Array = new Uint8Array(gridCols * gridRows);
        // Hit-test only the core region (cells that actually cover the source
        // viewBox). Buffer cells are populated afterwards by dilation.
        for (let row = 0; row < coreRows; row++) {
            const y = vbY + row * safeCellSize;
            const cellMaxY = y + safeCellSize;
            const gridRowIdx = row + bufferCells;
            const rowStart = gridRowIdx * gridCols;
            for (let col = 0; col < coreCols; col++) {
                const x = vbX + col * safeCellSize;
                const cellMaxX = x + safeCellSize;
                const gridColIdx = col + bufferCells;
                for (let i = 0; i < elements.length; i++) {
                    const e = elements[i];
                    // AABB cull: skip elements whose world-space bbox doesn't
                    // intersect this cell.
                    if (
                        e.maxX < x ||
                        e.minX > cellMaxX ||
                        e.maxY < y ||
                        e.minY > cellMaxY
                    ) {
                        continue;
                    }
                    if (elementOverlapsCell(e, x, y, safeCellSize)) {
                        grid[rowStart + gridColIdx] = 1;
                        break;
                    }
                }
            }
        }

        if (bufferCells > 0) {
            grid = dilateGrid(grid, gridCols, gridRows, bufferCells);
        }

        const out: MarkedCell[] = [];
        for (let row = 0; row < gridRows; row++) {
            const rowStart = row * gridCols;
            const y = gridOriginY + row * safeCellSize;
            for (let col = 0; col < gridCols; col++) {
                if (grid[rowStart + col]) {
                    out.push({ x: gridOriginX + col * safeCellSize, y });
                }
            }
        }
        return out;
    }, [
        mountedGeom,
        vbX,
        vbY,
        coreCols,
        coreRows,
        gridCols,
        gridRows,
        gridOriginX,
        gridOriginY,
        safeCellSize,
        bufferCells,
    ]);

    const wrapOutlineSegments = useMemo(
        () =>
            computeWrapOutlineSegments(
                markedCells,
                gridOriginX,
                gridOriginY,
                safeCellSize,
                gridCols,
                gridRows,
            ),
        [markedCells, gridOriginX, gridOriginY, safeCellSize, gridCols, gridRows],
    );

    const canExport = !!inputSvg;

    const handleSvgLoad = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;
            setError(null);
            if (
                !file.type.includes("svg") &&
                !file.name.toLowerCase().endsWith(".svg")
            ) {
                setError("Please choose an SVG file.");
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                const text = typeof reader.result === "string" ? reader.result : "";
                if (!text) {
                    setError("Unable to read SVG file.");
                    return;
                }
                if (!isValidSvg(text)) {
                    setError("Selected file does not appear to be valid SVG.");
                    return;
                }
                setInputSvg({
                    name: file.name,
                    content: text,
                });
            };
            reader.onerror = () => setError("Error reading file.");
            reader.readAsText(file);
        },
        [],
    );

    useSvgToolHandoffReceiver(
        "grid-cell-wrapper",
        {
            onPrimary: (svg) => {
                setError(null);
                setInputSvg(svg);
            },
        },
        { setError },
    );

    const buildExportSvgString = useCallback(() => {
        if (!inputSvg) return "";
        const svgInner = inputSvg.content.replace(/<\?xml[^>]*\?>/gi, "").replace(/<\/?svg[^>]*>/g, "").trim();
        const strokeOpacity = clamp01(gridStrokeOpacity);
        const fillOpacity = clamp01(markedFillOpacity);

        const gridCells = markedCells
            .map(
                (c) =>
                    `    <rect x="${c.x}" y="${c.y}" width="${safeCellSize}" height="${safeCellSize}" fill="${markedFill}" fill-opacity="${fillOpacity}" />`,
            )
            .join("\n");

        const outlineBlock =
            wrapStrokeActive && wrapOutlineSegments.length > 0
                ? [
                      `  <g class="grid-cell-wrapper-wrap-outline" fill="none" stroke="${wrapStroke}" stroke-width="${safeWrapStrokeWidth}" stroke-opacity="${wrapStrokeOpacityClamped}" stroke-linecap="square" stroke-linejoin="miter">`,
                      ...wrapOutlineSegments.map(
                          (s) =>
                              `    <line x1="${s.x1}" y1="${s.y1}" x2="${s.x2}" y2="${s.y2}" />`,
                      ),
                      `  </g>`,
                  ].join("\n")
                : "";

        const gridLines: string[] = [];
        const gridEndX = gridOriginX + gridWidth;
        const gridEndY = gridOriginY + gridHeight;
        for (let col = 0; col <= gridCols; col++) {
            const x = gridOriginX + col * safeCellSize;
            gridLines.push(
                `    <line x1="${x}" y1="${gridOriginY}" x2="${x}" y2="${gridEndY}" stroke="${gridStroke}" stroke-opacity="${strokeOpacity}" stroke-width="1" />`,
            );
        }
        for (let row = 0; row <= gridRows; row++) {
            const y = gridOriginY + row * safeCellSize;
            gridLines.push(
                `    <line x1="${gridOriginX}" y1="${y}" x2="${gridEndX}" y2="${y}" stroke="${gridStroke}" stroke-opacity="${strokeOpacity}" stroke-width="1" />`,
            );
        }

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="grid-cell-wrapper-marked">
${gridCells}
  </g>
  <g class="grid-cell-wrapper-lines">
${gridLines.join("\n")}
  </g>
${outlineBlock}
  <g class="grid-cell-wrapper-source">
${svgInner}
  </g>
</svg>`;
    }, [
        inputSvg,
        markedCells,
        safeCellSize,
        markedFill,
        markedFillOpacity,
        gridStroke,
        gridStrokeOpacity,
        gridCols,
        gridRows,
        gridOriginX,
        gridOriginY,
        gridWidth,
        gridHeight,
        viewBoxAttr,
        wrapStroke,
        safeWrapStrokeWidth,
        wrapStrokeOpacityClamped,
        wrapStrokeActive,
        wrapOutlineSegments,
    ]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const baseName = inputSvg?.name
            ? inputSvg.name.replace(/\.[^.]+$/, "")
            : "grid-cell-wrapper";
        link.download = `${baseName}-grid-cell-wrapper.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, canExport, inputSvg]);

    const value: GridCellWrapperState = {
        inputSvg,
        cellSize: safeCellSize,
        bufferCells,
        gridStroke,
        gridStrokeOpacity,
        markedFill,
        markedFillOpacity,
        wrapStroke,
        wrapStrokeWidth: safeWrapStrokeWidth,
        wrapStrokeOpacity: wrapStrokeOpacityClamped,
        wrapStrokeActive,
        wrapOutlineSegments,
        markedCells,
        gridCols,
        gridRows,
        viewBoxAttr,
        canExport,
        error,
        setCellSize,
        setBufferCells,
        setGridStroke,
        setGridStrokeOpacity,
        setMarkedFill,
        setMarkedFillOpacity,
        setWrapStroke,
        setWrapStrokeWidth,
        setWrapStrokeOpacity,
        setError,
        handleSvgLoad,
        buildExportSvgString,
        handleDownload,
        inputRef,
    };

    return (
        <GridCellWrapperContext.Provider value={value}>
            {children}
        </GridCellWrapperContext.Provider>
    );
}
